import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Settings, 
  Webhook, 
  Copy, 
  Check,
  ExternalLink,
  CheckCircle2,
  Info,
  Code,
  FileJson,
  Table,
  List,
  Plus,
  Trash2,
  Save,
  Users,
  CreditCard,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [activeTab, setActiveTab] = useState('webhook');
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [masterLists, setMasterLists] = useState({});
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState({});
  
  // Editable list states
  const [editingLists, setEditingLists] = useState({});
  const [newItems, setNewItems] = useState({});
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchWebhookInfo();
    fetchMasterLists();
  }, []);

  const fetchWebhookInfo = async () => {
    try {
      const response = await axios.get(`${API}/settings/webhook-info`, {
        headers: getAuthHeaders()
      });
      setWebhookInfo(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch webhook info:', error);
    }
  };

  const fetchMasterLists = async () => {
    try {
      const response = await axios.get(`${API}/settings/master-lists`, {
        headers: getAuthHeaders()
      });
      const listsMap = {};
      response.data.forEach(list => {
        listsMap[list.list_type] = list;
      });
      setMasterLists(listsMap);
      setEditingLists(JSON.parse(JSON.stringify(listsMap)));
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch master lists:', error);
      toast.error('Failed to load master lists');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleAddItem = (listType) => {
    const newItem = newItems[listType]?.trim();
    if (!newItem) return;
    
    const existingList = editingLists[listType] || { items: [] };
    const newValue = newItem.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
    
    const updatedList = {
      ...existingList,
      items: [
        ...(existingList.items || []),
        { value: newValue, label: newItem, is_active: true }
      ]
    };
    
    setEditingLists({ ...editingLists, [listType]: updatedList });
    setNewItems({ ...newItems, [listType]: '' });
  };

  const handleRemoveItem = (listType, index) => {
    const existingList = editingLists[listType] || { items: [] };
    const updatedList = {
      ...existingList,
      items: (existingList.items || []).filter((_, i) => i !== index)
    };
    setEditingLists({ ...editingLists, [listType]: updatedList });
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const openDeleteDialog = (listType, index, itemLabel) => {
    setItemToDelete({ listType, index, itemLabel });
    setDeleteDialogOpen(true);
  };

  const handleSaveList = async (listType) => {
    try {
      setSaving({ ...saving, [listType]: true });
      const listItems = editingLists[listType]?.items || [];
      await axios.put(`${API}/settings/master-lists/${listType}`, {
        list_type: listType,
        items: listItems
      }, {
        headers: getAuthHeaders()
      });
      toast.success('List saved successfully');
      fetchMasterLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save list');
    } finally {
      setSaving({ ...saving, [listType]: false });
    }
  };

  const hasChanges = (listType) => {
    const original = masterLists[listType]?.items || [];
    const edited = editingLists[listType]?.items || [];
    return JSON.stringify(original) !== JSON.stringify(edited);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-950"></div>
      </div>
    );
  }

  const expectedFields = webhookInfo?.expected_fields || {};
  const samplePayload = webhookInfo?.sample_payload || {};

  const listConfigs = [
    { 
      type: 'submitted_by', 
      title: 'Submitted By', 
      description: 'People who can submit expenses',
      icon: Users,
      placeholder: 'Enter name...'
    },
    { 
      type: 'payment_modes', 
      title: 'Payment Modes', 
      description: 'Available payment methods',
      icon: CreditCard,
      placeholder: 'Enter payment mode...'
    },
    { 
      type: 'payment_types', 
      title: 'Payment Types', 
      description: 'Categories for expense payments',
      icon: FileText,
      placeholder: 'Enter payment type...'
    },
    { 
      type: 'categories', 
      title: 'HR Expense Categories', 
      description: 'Categories for HR expenses (Recruitment, Benefits, etc.)',
      icon: List,
      placeholder: 'Enter category...'
    }
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950 flex items-center gap-3">
            <Settings className="w-7 h-7" />
            Settings
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Configure integrations and system settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="webhook" className="data-[state=active]:bg-white">
            <Webhook className="w-4 h-4 mr-2" />
            Webhook Configuration
          </TabsTrigger>
          <TabsTrigger value="master-lists" className="data-[state=active]:bg-white">
            <List className="w-4 h-4 mr-2" />
            Master Lists
          </TabsTrigger>
        </TabsList>

        {/* Webhook Tab */}
        <TabsContent value="webhook" className="space-y-6">
          {/* Webhook Configuration */}
          <Card className="border-slate-200" data-testid="webhook-config-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                    <Webhook className="w-5 h-5" />
                    Zoho Forms + Pabbly Integration
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Configure Pabbly to send Zoho Forms data to this webhook endpoint
                  </CardDescription>
                </div>
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Active
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Webhook URL */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Webhook URL (use in Pabbly)</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      value={webhookInfo?.webhook_url || ''}
                      readOnly
                      className="pr-10 font-mono text-sm bg-slate-50"
                      data-testid="webhook-url-input"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => copyToClipboard(webhookInfo?.webhook_url)}
                      data-testid="copy-webhook-btn"
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Configuration Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Method</p>
                  <p className="font-mono font-semibold text-indigo-950 mt-1">{webhookInfo?.method}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Content Type</p>
                  <p className="font-mono font-semibold text-indigo-950 mt-1">{webhookInfo?.content_type}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Status</p>
                  <p className="font-semibold text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    {webhookInfo?.status}
                  </p>
                </div>
              </div>

              {/* Setup Instructions */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold text-slate-900">Pabbly Setup Instructions</h3>
                </div>
                <ol className="space-y-2 ml-7">
                  {webhookInfo?.instructions?.map((instruction, index) => (
                    <li key={`inst-${index}`} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-semibold">
                        {index + 1}
                      </span>
                      <span>{instruction.replace(/^\d+\.\s*/, '')}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Expected Fields Table */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <Table className="w-5 h-5" />
                Zoho Form Field Mapping
              </CardTitle>
              <CardDescription>
                Map these Zoho Form fields in your Pabbly workflow
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">Zoho Field Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">Type & Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(expectedFields).map(([field, description], index) => (
                      <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <code className="bg-slate-100 px-2 py-1 rounded text-indigo-700 font-mono text-xs">
                            {field}
                          </code>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Sample Payload */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                    <Code className="w-5 h-5" />
                    Sample JSON Payload
                  </CardTitle>
                  <CardDescription>
                    Configure Pabbly to send this JSON structure to the webhook
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(JSON.stringify(samplePayload, null, 2))}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy JSON
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                {JSON.stringify(samplePayload, null, 2)}
              </pre>
            </CardContent>
          </Card>

          {/* Integration Flow Diagram */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <FileJson className="w-5 h-5" />
                Integration Flow
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center gap-4 py-4 flex-wrap">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center min-w-[120px]">
                  <p className="text-xs text-blue-500 font-semibold">STEP 1</p>
                  <p className="font-semibold text-blue-900">Zoho Forms</p>
                  <p className="text-xs text-blue-600">User submits</p>
                </div>
                <div className="text-slate-400">→</div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center min-w-[120px]">
                  <p className="text-xs text-purple-500 font-semibold">STEP 2</p>
                  <p className="font-semibold text-purple-900">Pabbly</p>
                  <p className="text-xs text-purple-600">Trigger & Map</p>
                </div>
                <div className="text-slate-400">→</div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center min-w-[120px]">
                  <p className="text-xs text-emerald-500 font-semibold">STEP 3</p>
                  <p className="font-semibold text-emerald-900">CostLedger</p>
                  <p className="text-xs text-emerald-600">Webhook API</p>
                </div>
                <div className="text-slate-400">→</div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center min-w-[120px]">
                  <p className="text-xs text-indigo-500 font-semibold">STEP 4</p>
                  <p className="font-semibold text-indigo-900">Database</p>
                  <p className="text-xs text-indigo-600">Expense saved</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Test Webhook Link */}
          {user?.role === 'ADMIN' && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ExternalLink className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900">Test Your Integration</h3>
                      <p className="text-sm text-blue-700">Submit a test form in Zoho, then check Webhook Logs to verify.</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => window.location.href = '/admin/webhooks'}
                  >
                    View Webhook Logs
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Master Lists Tab */}
        <TabsContent value="master-lists" className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                <List className="w-5 h-5" />
                Master Lists Configuration
              </CardTitle>
              <CardDescription>
                Configure dropdown values for expense forms. Changes will apply to all new entries.
              </CardDescription>
            </CardHeader>
          </Card>

          {listConfigs.map(config => {
            const IconComponent = config.icon;
            const listData = editingLists[config.type] || { items: [] };
            const isModified = hasChanges(config.type);
            
            return (
              <Card key={config.type} className="border-slate-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-heading text-indigo-950 flex items-center gap-2">
                        <IconComponent className="w-5 h-5" />
                        {config.title}
                      </CardTitle>
                      <CardDescription>{config.description}</CardDescription>
                    </div>
                    {user?.role === 'ADMIN' && (
                      <Button
                        onClick={() => handleSaveList(config.type)}
                        disabled={!isModified || saving[config.type]}
                        className={isModified ? 'bg-indigo-950 hover:bg-indigo-900' : ''}
                      >
                        {saving[config.type] ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Save Changes
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add new item */}
                  {user?.role === 'ADMIN' && (
                    <div className="flex gap-2">
                      <Input
                        placeholder={config.placeholder}
                        value={newItems[config.type] || ''}
                        onChange={(e) => setNewItems({ ...newItems, [config.type]: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddItem(config.type)}
                        className="flex-1"
                      />
                      <Button onClick={() => handleAddItem(config.type)} variant="outline">
                        <Plus className="w-4 h-4 mr-2" />
                        Add
                      </Button>
                    </div>
                  )}
                  
                  {/* Items list */}
                  <div className="space-y-2">
                    {listData.items?.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">
                        No items configured. Add some above.
                      </p>
                    ) : (
                      listData.items?.map((item, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-semibold">
                              {index + 1}
                            </span>
                            <span className="font-medium text-slate-900">{item.label}</span>
                            <code className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded">
                              {item.value}
                            </code>
                          </div>
                          {user?.role === 'ADMIN' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openDeleteDialog(config.type, index, item.label)}
                              data-testid={`delete-item-${config.type}-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Entry
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to delete <strong className="text-slate-900">"{itemToDelete?.itemLabel}"</strong> from the master list.
              <br /><br />
              This action will remove the entry immediately. You'll need to save changes to make it permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => itemToDelete && handleRemoveItem(itemToDelete.listType, itemToDelete.index)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SettingsPage;
